import type {
  AddLabel,
  AddMember,
  HistoryAction,
  HistoryActionType,
  HistoryContext,
  Join,
  Leave,
  Move,
  RemoveLabel,
  RemoveMember,
  Rename,
  SetDates,
  UpdateDescription,
} from '@trello/action-history';
import type {
  ActionSubjectType,
  SourceType,
  Task,
} from '@trello/atlassian-analytics';
import { Analytics, tracingCallback } from '@trello/atlassian-analytics';
import { featureFlagClient } from '@trello/feature-flag-client';
import { client } from '@trello/graphql';
import { forNamespace } from '@trello/i18n';
import type { Board, Card } from '@trello/model-types';
import { dangerouslyConvertPrivacyString } from '@trello/privacy';

import { ModelCache } from 'app/scripts/db/ModelCache';
import type { Board as LegacyBoardModel } from 'app/scripts/models/Board';
import type { Card as LegacyCardModel } from 'app/scripts/models/Card';
import type { Label } from 'app/scripts/models/Label';
import type { List } from 'app/scripts/models/List';
import type { Member } from 'app/scripts/models/Member';
import { BoardListsContextCardFragmentDoc } from 'app/src/components/BoardListsContext/BoardListsContextCardFragment.generated';
import { calculateItemPosition } from 'app/src/components/BoardListView/calculateItemPosition';
import { fireCardFrontConfetti } from 'app/src/components/BoardListView/fireCardFrontConfetti';
import { getListElement } from 'app/src/components/BoardListView/getListsTreeWalker';
import { ListNameFragmentDoc } from 'app/src/components/List/ListNameFragment.generated';
import { readListVisibleCardsFromCache } from 'app/src/components/List/readListVisibleCardsFromCache';
import type { CardActionFragment } from './CardActionFragment.generated';
import { CardActionFragmentDoc } from './CardActionFragment.generated';
import { CardActionDocument } from './CardActionMutation.generated';
import { LabelNameFragmentDoc } from './LabelNameFragment.generated';
import { MemberNameFragmentDoc } from './MemberNameFragment.generated';
import { recordCardAction } from './recordCardAction';

const format = forNamespace('notificationsGrouped', {
  shouldEscapeStrings: false,
});

function onTracedCardUpdate({
  card,
  traceId,
  taskName,
  source,
  field,
}: {
  card: LegacyCardModel;
  traceId: string;
  taskName: Task;
  source: SourceType;
  field: ActionSubjectType;
}) {
  return tracingCallback(
    {
      taskName,
      traceId,
      source,
    },
    () => {
      Analytics.sendUpdatedCardFieldEvent({
        field,
        source,
        containers: {
          card: { id: card.id },
          board: { id: card.get('idBoard') },
          list: { id: card.get('idList') },
        },
        attributes: {
          taskId: traceId,
        },
      });
    },
  );
}

export class NoopError extends Error {
  constructor() {
    super('noop');
    this.name = 'NoopError';
  }
}

function getCached(type: 'Board', id: string): LegacyBoardModel;
function getCached(type: 'Label', id: string): Label;
function getCached(type: 'List', id: string): List;
function getCached(type: 'Member', id: string): Member;
function getCached(
  type: 'Member' | 'List' | 'Board' | 'Label',
  id: string,
): Member | List | LegacyBoardModel | Label {
  // @ts-expect-error
  const cached = ModelCache.get(type, id);
  if (!cached) {
    // If model is not in cache, that's fine; a user could be trying to undo or
    // redo an action from long ago that is no longer referenceable in cache.
    throw new NoopError();
  }
  return cached;
}

export type ActionMap = {
  [type in HistoryActionType]?: (
    card: LegacyCardModel,
    action: HistoryAction,
    trace: {
      traceId: string;
      taskName: Task;
      field: ActionSubjectType;
      source: SourceType;
    },
    context: HistoryContext,
  ) => string | undefined;
};

export const doMap: ActionMap = {
  // @ts-expect-error
  join: (card, action: Join, trace) => {
    const useGraphQlActions = featureFlagClient.get(
      'xf.undo-redo-rewrite',
      false,
    );

    const cardId = card.id;
    const memberId = action.idMember;

    const { traceId } = trace;

    if (useGraphQlActions) {
      const cardFragment = client.readFragment<CardActionFragment>({
        id: `Card:${cardId}`,
        fragment: CardActionFragmentDoc,
      });

      if (!cardFragment) {
        return;
      }

      if (cardFragment.idMembers.includes(memberId)) {
        throw new NoopError();
      }

      const newMembers = [...cardFragment.idMembers, memberId];

      recordCardAction(cardId, action);

      client.mutate({
        mutation: CardActionDocument,
        variables: {
          cardId,
          card: {
            idMembers: newMembers,
          },
          traceId,
        },
        optimisticResponse: {
          __typename: 'Mutation',
          updateCard: {
            __typename: 'Card',
            id: card.id,
            subscribed: true,
            idMembers: newMembers,
          },
        },
      });
    } else {
      if (card.hasMember(action.idMember)) {
        throw new NoopError();
      }
      card.addMemberWithTracing(
        action.idMember,
        traceId,
        onTracedCardUpdate({ card, ...trace }),
      );
    }

    return format('notification_joined_card');
  },
  // @ts-expect-error
  leave: (card, action: Leave, trace) => {
    const useGraphQlActions = featureFlagClient.get(
      'xf.undo-redo-rewrite',
      false,
    );

    const cardId = card.id;
    const memberId = action.idMember;
    const { traceId } = trace;

    if (useGraphQlActions) {
      const cardFragment = client.readFragment({
        id: `Card:${cardId}`,
        fragment: CardActionFragmentDoc,
      });

      if (!cardFragment.idMembers.includes(memberId)) {
        throw new NoopError();
      }

      const newMembers = cardFragment.idMembers.filter(
        (id: string) => id !== memberId,
      );

      recordCardAction(cardId, action);

      client.mutate({
        mutation: CardActionDocument,
        variables: {
          cardId,
          card: {
            idMembers: newMembers,
          },
          traceId,
        },
        optimisticResponse: {
          __typename: 'Mutation',
          updateCard: {
            __typename: 'Card',
            id: card.id,
            subscribed: false,
            idMembers: newMembers,
          },
        },
      });
    } else {
      if (!card.hasMember(action.idMember)) {
        throw new NoopError();
      }
      card.removeMemberWithTracing(
        action.idMember,
        traceId,
        onTracedCardUpdate({ card, ...trace }),
      );
    }

    return format('notification_left_card');
  },
  // @ts-expect-error
  'add-member': (card, action: AddMember, trace) => {
    const useGraphQlActions = featureFlagClient.get(
      'xf.undo-redo-rewrite',
      false,
    );

    const cardId = card.id;
    const memberId = action.idMember;
    const { traceId } = trace;

    if (useGraphQlActions) {
      const cardFragment = client.readFragment<CardActionFragment>({
        id: `Card:${cardId}`,
        fragment: CardActionFragmentDoc,
      });

      if (!cardFragment || cardFragment.idMembers.includes(memberId)) {
        throw new NoopError();
      }

      const newMembers = [...cardFragment.idMembers, memberId];

      recordCardAction(cardId, action);

      // don't await so we don't block the UI
      client.mutate({
        mutation: CardActionDocument,
        variables: {
          cardId,
          card: {
            idMembers: newMembers,
          },
          traceId,
        },
        optimisticResponse: {
          __typename: 'Mutation',
          updateCard: {
            __typename: 'Card',
            id: card.id,
            subscribed: true,
            idMembers: newMembers,
          },
        },
      });
    } else {
      if (card.hasMember(action.idMember)) {
        throw new NoopError();
      }
      card.addMemberWithTracing(
        action.idMember,
        traceId,
        onTracedCardUpdate({ card, ...trace }),
      );
    }

    const memberFragment = client.readFragment({
      id: `Member:${memberId}`,
      fragment: MemberNameFragmentDoc,
    });
    return format('notification_added_member_to_card', {
      member: dangerouslyConvertPrivacyString(memberFragment.fullName),
    });
  },
  // @ts-expect-error
  'remove-member': (card, action: RemoveMember, trace) => {
    const useGraphQlActions = featureFlagClient.get(
      'xf.undo-redo-rewrite',
      false,
    );

    const cardId = card.id;
    const memberId = action.idMember;
    const { traceId } = trace;

    if (useGraphQlActions) {
      const cardFragment = client.readFragment({
        id: `Card:${cardId}`,
        fragment: CardActionFragmentDoc,
      });

      if (!cardFragment.idMembers.includes(memberId)) {
        throw new NoopError();
      }

      const newMembers = cardFragment.idMembers.filter(
        (id: string) => id !== memberId,
      );

      recordCardAction(cardId, action);

      // don't await so we don't block the UI
      client.mutate({
        mutation: CardActionDocument,
        variables: {
          cardId,
          card: {
            idMembers: newMembers,
          },
          traceId,
        },
        optimisticResponse: {
          __typename: 'Mutation',
          updateCard: {
            __typename: 'Card',
            id: card.id,
            subscribed: false,
            idMembers: newMembers,
          },
        },
      });
    } else {
      if (!card.hasMember(action.idMember)) {
        throw new NoopError();
      }
      card.removeMemberWithTracing(
        action.idMember,
        traceId,
        onTracedCardUpdate({ card, ...trace }),
      );
    }

    const memberFragment = client.readFragment({
      id: `Member:${memberId}`,
      fragment: MemberNameFragmentDoc,
    });
    return format('notification_added_member_to_card', {
      member: dangerouslyConvertPrivacyString(memberFragment.fullName),
    });
  },
  // @ts-expect-error
  move: (card, action: Move, trace: Trace) => {
    const useGraphQlActions = featureFlagClient.get(
      'xf.undo-redo-rewrite',
      false,
    );
    const cardId = card.id;

    const { traceId } = trace;

    if (useGraphQlActions) {
      const cardFragment = client.readFragment<CardActionFragment>({
        id: `Card:${cardId}`,
        fragment: CardActionFragmentDoc,
      });

      const cardContext = client.readFragment({
        id: `Card:${cardId}`,
        fragment: BoardListsContextCardFragmentDoc,
      });

      if (!cardFragment || !cardContext) {
        throw new NoopError();
      }

      const boardId = cardFragment.idBoard;
      const prevListId = cardFragment.idList;

      const nextListId = action.idList;

      const sourceCards = readListVisibleCardsFromCache({
        boardId,
        listId: prevListId,
      });

      const destCards = readListVisibleCardsFromCache({
        boardId,
        listId: nextListId,
      });

      const sourceIndex =
        sourceCards?.findIndex(({ id }) => id === cardId) ?? 1e9;

      let targetIndex: number = 0;
      if (action.position === 'top') {
        targetIndex = 0;
      } else if (action.position === 'bottom') {
        targetIndex = destCards.length;
      } else if (typeof action.position === 'number') {
        // If the position is arbitrary, it probably doesn't matter too much, but
        // drop it underneath the last card just in case.
        targetIndex = action.position + 1;
      }

      const position = calculateItemPosition(
        targetIndex,
        destCards,
        cardContext,
      );

      const numCardsInTargetList = destCards.length;

      const recordedPosition = (() => {
        switch (targetIndex) {
          case 0:
            if (numCardsInTargetList > 0) {
              return 'top';
            } else {
              return targetIndex;
            }
          case numCardsInTargetList:
            if (numCardsInTargetList > 1) {
              return 'bottom';
            } else {
              return targetIndex;
            }
          case 1:
            if (numCardsInTargetList > 1) {
              return 1;
            } else {
              return targetIndex;
            }
          default:
            return targetIndex;
        }
      })();

      const newAction: Move = {
        type: 'move',
        idList: nextListId,
        fromPosition: sourceIndex,
        position: recordedPosition,
        idBoard: boardId,
      };

      recordCardAction(cardId, newAction);

      const nextListElement = getListElement(nextListId);
      if (nextListElement) {
        fireCardFrontConfetti({ listId: nextListId }, nextListElement);
      }

      // don't await so we don't block the UI
      client.mutate({
        mutation: CardActionDocument,
        variables: {
          cardId,
          card: {
            idList: nextListId,
            pos: position,
            idBoard: boardId,
          },
          traceId,
        },
        optimisticResponse: {
          __typename: 'Mutation',
          updateCard: {
            id: cardId,
            idList: nextListId,
            pos: position,
            __typename: 'Card',
          },
        },
      });
    } else {
      const list = getCached('List', action.idList);

      // In the case of repeatAction, `context` won't match the given card,
      // so check the card's original location rather than using `context`.
      const prevIdBoard = card.get('idBoard');
      const prevIdList = card.get('idList');
      let index;
      if (action.position === 'top') {
        index = 0;
      } else if (action.position === 'bottom') {
        index = list.openCards().length;
      } else {
        // If the position is arbitrary, it probably doesn't matter too much, but
        // drop it underneath the last card just in case.
        index = action.position + 1;
      }
      card.moveToList(list, index);
      if (prevIdBoard !== action.idBoard) {
        return format('notification_moved_card_to_board', {
          list: list.get('name'),
          board: getCached('Board', action.idBoard)?.get('name'),
        });
      }
      if (prevIdList === action.idList) {
        return format('notification_moved_card_within_list', {
          list: list.get('name'),
        });
      }
    }
    const listFragment = client.readFragment({
      id: `Card:${cardId}`,
      fragment: ListNameFragmentDoc,
    });

    return format('notification_moved_card', { list: listFragment?.name });
  },
  // @ts-expect-error
  'add-label': (card, action: AddLabel, trace: Trace) => {
    const useGraphQlActions = featureFlagClient.get(
      'xf.undo-redo-rewrite',
      false,
    );

    const cardId = card.id;
    const labelId = action.idLabel;
    const { traceId } = trace;

    /**
     * keeping this here since we need it in the else block,
     * but will be converted to a fragment after rollout
     */
    const label = getCached('Label', action.idLabel) as Label;

    if (useGraphQlActions) {
      const cardFragment = client.readFragment<CardActionFragment>({
        id: `Card:${cardId}`,
        fragment: CardActionFragmentDoc,
      });

      if (!cardFragment || cardFragment.idLabels.includes(labelId)) {
        throw new NoopError();
      }

      const newLabels = [...cardFragment.idLabels, labelId];

      recordCardAction(cardId, action);

      // don't await so we don't block the UI
      client.mutate({
        mutation: CardActionDocument,
        variables: {
          cardId,
          card: {
            idLabels: newLabels,
          },
          traceId,
        },
        optimisticResponse: {
          __typename: 'Mutation',
          updateCard: {
            __typename: 'Card',
            id: cardId,
            idLabels: newLabels,
          },
        },
        update(cache, { data: optimisticData }) {
          if (!optimisticData) {
            return;
          }
          cache.modify<Card>({
            id: cache.identify({
              id: cardId,
              __typename: 'Card',
            }),
            fields: {
              labels(existingLabels = []) {
                if (!Array.isArray(existingLabels)) {
                  return existingLabels;
                }
                return [
                  ...existingLabels,
                  {
                    __ref: cache.identify({
                      id: labelId,
                      __typename: 'Label',
                    }),
                  },
                ];
              },
            },
          });
        },
      });
    } else {
      if (card.hasLabel(label)) {
        throw new NoopError();
      }
      card.toggleLabel(label, true);
    }

    const labelFragment = client.readFragment({
      id: `Label:${labelId}`,
      fragment: LabelNameFragmentDoc,
    });

    return format('notification_added_label_to_card', {
      label: labelFragment?.name,
    });
  },
  // @ts-expect-error
  'remove-label': (card, action: RemoveLabel, trace: Trace) => {
    const useGraphQlActions = featureFlagClient.get(
      'xf.undo-redo-rewrite',
      false,
    );

    const cardId = card.id;
    const labelId = action.idLabel;
    const { traceId } = trace;
    const label = getCached('Label', action.idLabel) as Label;

    if (useGraphQlActions) {
      const cardFragment = client.readFragment<CardActionFragment>({
        id: `Card:${cardId}`,
        fragment: CardActionFragmentDoc,
      });

      if (!cardFragment || !cardFragment.idLabels.includes(labelId)) {
        throw new NoopError();
      }

      const newLabels = cardFragment.idLabels.filter(
        (id: string) => id !== labelId,
      );

      recordCardAction(cardId, action);

      // don't await so we don't block the UI
      client.mutate({
        mutation: CardActionDocument,
        variables: {
          cardId,
          card: {
            idLabels: newLabels,
          },
          traceId,
        },
        optimisticResponse: {
          __typename: 'Mutation',
          updateCard: {
            __typename: 'Card',
            id: cardId,
            idLabels: newLabels,
          },
        },
        update(cache, { data: optimisticData }) {
          if (!optimisticData) {
            return;
          }
          cache.modify<Card>({
            id: cache.identify({
              id: cardId,
              __typename: 'Card',
            }),
            fields: {
              labels(existingLabels = [], { readField }) {
                const updatedLabels = existingLabels.filter((labelRef) => {
                  return readField('id', labelRef) !== labelId;
                });
                return updatedLabels;
              },
            },
          });
        },
      });
    } else {
      if (!card.hasLabel(label)) {
        throw new NoopError();
      }
      card.toggleLabel(label, false);
    }

    const labelFragment = client.readFragment({
      id: `Label:${labelId}`,
      fragment: LabelNameFragmentDoc,
    });

    return format('notification_removed_label_from_card', {
      label: labelFragment.name,
    });
  },
  archive: (card, action, trace) => {
    const useGraphQlActions = featureFlagClient.get(
      'xf.undo-redo-rewrite',
      false,
    );

    const cardId = card.id;
    const { traceId } = trace;

    if (useGraphQlActions) {
      const cardFragment = client.readFragment({
        id: `Card:${cardId}`,
        fragment: CardActionFragmentDoc,
      });

      if (cardFragment.closed) {
        throw new NoopError();
      }

      const boardId = cardFragment.idBoard;

      recordCardAction(cardId, action);

      // don't await so we don't block the UI
      client.mutate({
        mutation: CardActionDocument,
        variables: {
          cardId,
          card: {
            closed: true,
          },
          traceId,
        },
        optimisticResponse: {
          __typename: 'Mutation',
          updateCard: {
            __typename: 'Card',
            id: cardId,
            closed: true,
          },
        },
        update: (cache, { data: optimisticData }) => {
          if (!optimisticData?.updateCard) {
            return;
          }

          // Filter out the closed card from the board.cards array
          cache.modify<Board>({
            id: cache.identify({
              id: boardId,
              __typename: 'Board',
            }),
            fields: {
              cards(existingCards = [], { readField }) {
                const updatedCards = existingCards.filter((cardRef) => {
                  return readField('id', cardRef) !== cardId;
                });
                return updatedCards;
              },
            },
          });
        },
      });
    } else {
      if (card.get('closed')) {
        throw new NoopError();
      }
      card.close(traceId, onTracedCardUpdate({ card, ...trace }));
    }

    return format('notification_archived_card');
  },
  unarchive: (card, action, trace) => {
    const useGraphQlActions = featureFlagClient.get(
      'xf.undo-redo-rewrite',
      false,
    );

    const cardId = card.id;
    const { traceId } = trace;

    if (useGraphQlActions) {
      const cardFragment = client.readFragment({
        id: `Card:${cardId}`,
        fragment: CardActionFragmentDoc,
      });

      if (!cardFragment.closed) {
        throw new NoopError();
      }

      recordCardAction(cardId, action);

      // don't await so we don't block the UI
      client.mutate({
        mutation: CardActionDocument,
        variables: {
          cardId,
          card: {
            closed: false,
          },
          traceId,
        },
        optimisticResponse: {
          __typename: 'Mutation',
          updateCard: {
            __typename: 'Card',
            id: cardId,
            closed: false,
          },
        },
      });
    } else {
      if (!card.get('closed')) {
        throw new NoopError();
      }
      // @ts-expect-error
      card.reopen(traceId, onTracedCardUpdate({ card, ...trace }));
    }

    return format('notification_unarchived_card');
  },
  // @ts-expect-error
  'set-dates': (card, action: SetDates, trace) => {
    const useGraphQlActions = featureFlagClient.get(
      'xf.undo-redo-rewrite',
      false,
    );

    if (useGraphQlActions) {
      const cardId = card.id;
      const { traceId } = trace;

      const cardFragment = client.readFragment<CardActionFragment>({
        id: `Card:${cardId}`,
        fragment: CardActionFragmentDoc,
      });
      if (!cardFragment) {
        throw new NoopError();
      }

      const previousDates: SetDates['previousDates'] = {
        start: cardFragment.start
          ? new Date(cardFragment.start).getTime()
          : null,
        due: cardFragment.due ? new Date(cardFragment.due).getTime() : null,
        dueReminder: cardFragment.dueReminder || -1,
      };

      recordCardAction(cardId, { ...action, previousDates });

      const { dates } = action;
      if (
        previousDates.start === dates.start &&
        previousDates.due === dates.due &&
        previousDates.dueReminder === dates.dueReminder
      ) {
        throw new NoopError();
      }

      // we need our dates to be strings
      const startDate = dates.start ? new Date(dates.start).toISOString() : '';

      const dueDate = dates.due ? new Date(dates.due).toISOString() : '';

      client.mutate({
        mutation: CardActionDocument,
        variables: {
          cardId,
          card: {
            start: startDate,
            due: dueDate,
            dueReminder: dates.dueReminder,
          },
          traceId,
        },
        optimisticResponse: {
          __typename: 'Mutation',
          updateCard: {
            __typename: 'Card',
            id: cardId,
            start: startDate,
            due: dueDate,
            dueReminder: dates.dueReminder,
          },
        },
      });
    } else {
      // Get field from card rather than `action.previousDates` for repeat.
      const previousDates: SetDates['previousDates'] = {
        start: card.getStartDate()?.getTime() ?? null,
        due: card.getDueDate()?.getTime() ?? null,
        dueReminder: card.get('dueReminder') || -1,
      };

      card.recordAction({ ...action, previousDates });

      const { dates } = action;
      if (
        previousDates.start === dates.start &&
        previousDates.due === dates.due &&
        previousDates.dueReminder === dates.dueReminder
      ) {
        throw new NoopError();
      }

      const { traceId } = trace;
      card.update(
        // @ts-expect-error
        { ...dates, traceId },
        onTracedCardUpdate({ card, ...trace }),
      );
    }

    return format('notification_changed_dates');
  },
  // @ts-expect-error
  rename: (card, action: Rename, trace: Trace) => {
    const useGraphQlActions = featureFlagClient.get<true | false>(
      'xf.undo-redo-rewrite',
      false,
    );
    if (useGraphQlActions) {
      const cardId = card.id;
      const { traceId } = trace;

      const cardFragment = client.readFragment({
        id: `Card:${cardId}`,
        fragment: CardActionFragmentDoc,
      });

      if (!cardFragment) {
        throw new NoopError();
      }

      recordCardAction(cardId, { ...action, previousName: cardFragment.name });

      // don't await so we don't block the UI
      client.mutate({
        mutation: CardActionDocument,
        variables: {
          cardId,
          card: {
            name: action.name,
          },
          traceId,
        },
        optimisticResponse: {
          __typename: 'Mutation',
          updateCard: {
            __typename: 'Card',
            id: cardId,
            name: action.name,
          },
        },
      });
    } else {
      card.recordAction({
        ...action,
        previousName: card.get('name'),
      });
      const { traceId } = trace;
      card.update(
        { name: action.name, traceId },
        onTracedCardUpdate({ card, ...trace }),
      );
    }

    return format('notification_renamed_card');
  },
  // @ts-expect-error
  'update-description': (card, action: UpdateDescription, trace) => {
    const useGraphQlActions = featureFlagClient.get(
      'xf.undo-redo-rewrite',
      false,
    );
    if (useGraphQlActions) {
      const cardId = card.id;
      const { traceId } = trace;

      recordCardAction(cardId, action);

      // don't await so we don't block the UI
      client.mutate({
        mutation: CardActionDocument,
        variables: {
          cardId,
          card: {
            desc: action.description,
          },
          traceId,
        },
        optimisticResponse: {
          __typename: 'Mutation',
          updateCard: {
            __typename: 'Card',
            id: cardId,
            desc: action.description,
          },
        },
      });
    } else {
      card.recordAction({
        ...action,
        previousDescription: card.get('desc'),
      });
      const { traceId } = trace;
      card.update(
        { desc: action.description, traceId },
        onTracedCardUpdate({ card, ...trace }),
      );
    }

    return format('notification_updated_description_of_card');
  },
};

export const undoMap: ActionMap = {
  join: doMap.leave,
  leave: doMap.join,
  'add-member': doMap['remove-member'],
  'remove-member': doMap['add-member'],
  // @ts-expect-error
  move: (card, action: Move, trace: Trace, context) => {
    const useGraphQlActions = featureFlagClient.get(
      'xf.undo-redo-rewrite',
      false,
    );

    const cardId = card.id;
    const { traceId } = trace;

    if (useGraphQlActions) {
      const nextListId = context.idList;
      const targetIndex = action.fromPosition;

      const cardFragment = client.readFragment<CardActionFragment>({
        id: `Card:${cardId}`,
        fragment: CardActionFragmentDoc,
      });

      const cardContext = client.readFragment({
        id: `Card:${cardId}`,
        fragment: BoardListsContextCardFragmentDoc,
      });

      if (!cardFragment || !cardContext) {
        throw new NoopError();
      }

      const boardId = cardFragment.idBoard;
      const prevListId = cardFragment.idList;

      const sourceCards = readListVisibleCardsFromCache({
        boardId,
        listId: prevListId,
      });

      const destCards = readListVisibleCardsFromCache({
        boardId,
        listId: nextListId,
      });

      const sourceIndex =
        sourceCards?.findIndex(({ id }) => id === cardId) ?? 1e9;

      const position = calculateItemPosition(
        targetIndex,
        destCards,
        cardContext,
      );

      const numCardsInTargetList = destCards.length;

      const recordedPosition = (() => {
        switch (targetIndex) {
          case 0:
            if (numCardsInTargetList > 0) {
              return 'top';
            } else {
              return targetIndex;
            }
          case numCardsInTargetList:
            if (numCardsInTargetList > 1) {
              return 'bottom';
            } else {
              return targetIndex;
            }
          case 1:
            if (numCardsInTargetList > 1) {
              return 1;
            } else {
              return targetIndex;
            }
          default:
            return targetIndex;
        }
      })();

      const newAction: Move = {
        type: 'move',
        idList: nextListId,
        fromPosition: sourceIndex,
        position: recordedPosition,
        idBoard: boardId,
      };

      recordCardAction(cardId, newAction);

      client.mutate({
        mutation: CardActionDocument,
        variables: {
          cardId,
          card: {
            idList: nextListId,
            pos: position,
            idBoard: boardId,
          },
          traceId,
        },
        optimisticResponse: {
          __typename: 'Mutation',
          updateCard: {
            id: cardId,
            idList: nextListId,
            pos: position,
            __typename: 'Card',
          },
        },
      });
    } else {
      const list = getCached('List', context.idList) as List;
      card.moveToList(list, action.fromPosition);
      if (action.idBoard !== context.idBoard) {
        return format('notification_moved_card_to_board', {
          list: list.get('name'),
          board: (getCached('Board', context.idBoard) as LegacyBoardModel).get(
            'name',
          ),
        });
      }
      if (action.idList === context.idList) {
        return format('notification_moved_card_within_list', {
          list: list.get('name'),
        });
      }
    }
    const listFragment = client.readFragment({
      id: `Card:${cardId}`,
      fragment: ListNameFragmentDoc,
    });

    return format('notification_moved_card', { list: listFragment?.name });
  },
  'add-label': doMap['remove-label'],
  'remove-label': doMap['add-label'],
  archive: doMap.unarchive,
  unarchive: doMap.archive,
  // @ts-expect-error
  'set-dates': (card, action: SetDates, trace, context) => {
    const invertedAction: SetDates = {
      ...action,
      dates: action.previousDates,
      previousDates: action.dates,
    };
    return doMap['set-dates']?.(card, invertedAction, trace, context);
  },
  // @ts-expect-error
  rename: (card, action: Rename, trace, context) => {
    const invertedAction: Rename = {
      ...action,
      name: action.previousName,
      previousName: action.name,
    };
    return doMap.rename?.(card, invertedAction, trace, context);
  },
  // @ts-expect-error
  'update-description': (card, action: UpdateDescription, trace, context) => {
    const invertedAction: UpdateDescription = {
      ...action,
      description: action.previousDescription,
      previousDescription: action.description,
    };
    return doMap['update-description']?.(card, invertedAction, trace, context);
  },
};
